import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Spin, Alert } from "antd"
import { TempLayout } from "../../layouts"
import { useSapSupplierStore } from "../../stores/sap-supplier/sapSupplierStore"
import SapSupplierDetailView from "./components/SapSupplierDetailView"
import ApprovalActions from "../../atoms/approval-actions"
import { ISapSanitizedSupplier } from "../../types"
import { getSupplierEditAccess } from "./utils/supplierEditAccess"


const SapSuppliersDetail = () => {
  const { recordId } = useParams()
  const navigate = useNavigate()
  const { fetchSapSupplierById, currentSupplier, isLoading, error, fetchSupplierAttachmentUrl } = useSapSupplierStore()
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({})
  const currentSession = useMemo(() => {
    try {
      return JSON.parse(window.localStorage.getItem("session_farms") || "null") as { employee_id?: string } | null
    } catch (_error) {
      return null
    }
  }, [])

  const sanitizedApprovalData = currentSupplier ? {
    status: 200,
    data: currentSupplier.approval_process_id
  } : undefined

  const sanitizedSupplierDetail: ISapSanitizedSupplier | null = currentSupplier ? {
    ...currentSupplier,
    approval_process_id: sanitizedApprovalData
  } : null
  const editAccess = getSupplierEditAccess(currentSupplier?.approval_process_id, currentSession?.employee_id || null)
  const canEditSupplier = editAccess.canEdit && (currentSupplier?.approval_status || "").toUpperCase() !== "APPROVED"


  const handleApproveClick = () => {
    navigate("/sapspl/history")
  }

  const fetchSupplierById = useCallback(async () => {
    if (recordId) {
      await fetchSapSupplierById(recordId)
    }
  }, [recordId, fetchSapSupplierById])

  useEffect(() => {
    void fetchSupplierById()
  }, [fetchSupplierById])

  useEffect(() => {
    const requesterName = currentSupplier?.fullname?.trim()
    const currentRecordId = currentSupplier?.record_id || recordId || ""
    document.title = requesterName ? `${requesterName} - SAP Supplier${currentRecordId ? ` (${currentRecordId})` : ""}` : "SAP - Supplier Detail"

    return () => {
      document.title = "FARMS Application"
    }
  }, [currentSupplier?.fullname, currentSupplier?.record_id, recordId])

  useEffect(() => {
    const attachments = currentSupplier?.attachments || []
    if (!recordId || !attachments.length) {
      setAttachmentUrls({})
      return
    }

    const loadAttachmentUrls = async () => {
      try {
        const attachmentEntries = await Promise.all(
          attachments
            .filter(attachment => attachment?.file_key)
            .map(async attachment => {
              const fileKey = String(attachment.file_key || "")
              const attachmentUrl = await fetchSupplierAttachmentUrl(recordId, fileKey)
              return [fileKey, attachmentUrl] as const
            }),
        )

        setAttachmentUrls(Object.fromEntries(attachmentEntries))
      } catch (err) {
        console.error("Failed to fetch attachment URLs:", err)
        setAttachmentUrls({})
      }
    }

    void loadAttachmentUrls()
  }, [recordId, currentSupplier?.attachments, fetchSupplierAttachmentUrl])


  return (
    <TempLayout title="SAP - Supplier Detail" navNavigateUrl="/sapspl/history">
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert message="Error" description={error} type="error" showIcon />
      ) : sanitizedSupplierDetail ? (
        <SapSupplierDetailView 
          data={sanitizedSupplierDetail} 
          attachmentUrls={attachmentUrls}
        />
      ) : (
        <Alert message="Not Found" description="Supplier not found or ID missing" type="warning" showIcon />
      )}

      {currentSupplier && (
        <ApprovalActions
          editNavigateUrl={`/sapspl/edit/${recordId}`}
          canEditData={canEditSupplier}
          mainData={sanitizedSupplierDetail}
          onApproveClick={handleApproveClick}
        />
      )}
    </TempLayout>
  )
}

export default SapSuppliersDetail

